import React, { useEffect, useMemo, useState } from 'react';
import {
    Card, CardHeader, CardTitle, CardDescription, CardContent
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
    Loader2,
    Bell,
    BellRing,
    Filter as FilterIcon,
    Search as SearchIcon,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    CheckCircle2,
    XCircle,
    Clock,
    Sparkles,
} from 'lucide-react';
import { agentNotificationsAPI } from '@/services/api';

const fa = new Intl.NumberFormat('fa-IR');
const faDateTime = (d?: string) => d ? new Date(d).toLocaleString('fa-IR') : '—';

type Notification = {
    id: number;
    title?: string;
    message?: string;
    type?: string;
    read_at?: string | null;
    created_at: string;
    entity_type?: string | null;
    entity_id?: number | null;
};

type ListResponse = {
    success?: boolean;
    items: Notification[];
    total: number;
    page: number;
    limit: number;
};

const NotificationsPage: React.FC = () => {
    const [items, setItems] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'read' | 'unread'>('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);

    const loadData = async (opts?: { page?: number; limit?: number }) => {
        try {
            setError('');
            if (!opts) setLoading(true);
            const res: ListResponse = await agentNotificationsAPI.list({
                page: opts?.page || page,
                limit: opts?.limit || pageSize,
                only_unread: false,
            });
            if (!res || res.success === false) throw new Error('خطا در دریافت اعلان‌ها');
            setItems(res.items || []);
            setTotal(res.total || 0);
        } catch (e: any) {
            setError(e?.message || 'خطا در دریافت اعلان‌ها');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { loadData(); }, []);
    useEffect(() => { loadData({ page: 1, limit: pageSize }); setPage(1); }, [pageSize]);

    const filtered = useMemo(() => {
        const text = q.trim().toLowerCase();
        return items.filter(n => {
            const title = (n.title || '').toLowerCase();
            const body = (n.message || '').toLowerCase();
            const type = (n.type || '').toLowerCase();
            const textMatch = !text || title.includes(text) || body.includes(text) || type.includes(text);
            const statusMatch = statusFilter === 'all' ? true : statusFilter === 'unread' ? !n.read_at : !!n.read_at;
            return textMatch && statusMatch;
        });
    }, [items, q, statusFilter]);

    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);
    const pageSlice = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    const stats = useMemo(() => {
        const unread = items.filter(i => !i.read_at).length;
        return { total: items.length, unread, read: items.length - unread };
    }, [items]);

    const markAllRead = async () => {
        try {
            const unreadIds = items.filter(n => !n.read_at).map(n => n.id);
            if (unreadIds.length === 0) return;
            await agentNotificationsAPI.markAsRead(unreadIds);
            setItems(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
        } catch (e) { console.error(e); }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50">
            <Loader2 className="h-12 w-12 animate-spin text-emerald-600" />
        </div>
    );

    if (error) return (
        <div className="p-6"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>
    );

    return (
        <div className="min-h-screen space-y-6 p-4 md:p-8 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50" dir="rtl">
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600 p-8 shadow-2xl">
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-2">
                                <BellRing className="h-8 w-8" /> اعلان‌ها
                            </h1>
                        </div>
                        <p className="text-emerald-100">پیام‌ها و رویدادهای مرتبط با سفارش‌ها، فاکتورها و لایسنس‌ها</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30">
                            <div className="text-xs opacity-90">کل</div>
                            <div className="text-2xl font-bold">{fa.format(stats.total)}</div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30">
                            <div className="text-xs opacity-90">خوانده نشده</div>
                            <div className="text-2xl font-bold text-amber-200">{fa.format(stats.unread)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm">
                <CardHeader className="py-4 bg-gradient-to-r from-emerald-50 to-emerald-50/70">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <FilterIcon className="h-5 w-5 text-emerald-700" />
                            <div>
                                <CardTitle className="text-emerald-900 text-lg">فیلترها</CardTitle>
                                <CardDescription className="text-emerald-700">جستجو و فیلتر بر اساس وضعیت</CardDescription>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" onClick={() => { setRefreshing(true); loadData(); }} disabled={refreshing}>
                                <RefreshCw className={`ml-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                                به‌روزرسانی
                            </Button>
                            <Button variant="outline" onClick={markAllRead}>
                                <CheckCircle2 className="ml-2 h-4 w-4" /> علامت زدن همه
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="space-y-2 lg:col-span-2">
                            <label className="text-emerald-900 font-medium text-sm">جستجو</label>
                            <div className="relative">
                                <SearchIcon className="absolute left-3 top-3.5 h-5 w-5 text-emerald-300" />
                                <Input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="جستجو..." className="pl-11 h-11 border-emerald-200 rounded-xl" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-emerald-900 font-medium text-sm">وضعیت</label>
                            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value as any); setPage(1); }} className="h-11 w-full rounded-xl border border-emerald-200 bg-white px-4 text-sm">
                                <option value="all">همه</option>
                                <option value="unread">فقط خوانده نشده</option>
                                <option value="read">فقط خوانده شده</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-between items-center gap-3 text-xs text-emerald-700 bg-emerald-50/60 border border-emerald-100 rounded-xl px-3 py-2">
                        <span>{fa.format(totalItems)} اعلان مطابق فیلترها</span>
                        <Button variant="ghost" size="sm" onClick={() => { setQ(''); setStatusFilter('all'); setPage(1); }}>ریست</Button>
                    </div>
                </CardContent>
            </Card>

            {/* List */}
            {items.length === 0 ? (
                <Card><CardContent className="text-center py-16"><Bell className="h-16 w-16 mx-auto mb-4 text-emerald-200" /><h3 className="text-xl font-semibold">هنوز اعلانی ندارید</h3></CardContent></Card>
            ) : filtered.length === 0 ? (
                <Card><CardContent className="text-center py-12"><XCircle className="h-12 w-12 mx-auto mb-3 text-amber-300" /><h3 className="text-lg font-semibold">موردی یافت نشد</h3></CardContent></Card>
            ) : (
                <div className="space-y-4">
                    {pageSlice.map(n => (
                        <div key={n.id} className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-emerald-50/70 to-white shadow-md p-4 md:p-5">
                            <div className="flex items-start gap-3">
                                <div className="mt-1">{n.read_at ? <Bell className="h-5 w-5 text-gray-400" /> : <BellRing className="h-5 w-5 text-emerald-500" />}</div>
                                <div className="space-y-1 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="text-sm md:text-base font-semibold text-gray-900">{n.title}</h3>
                                        {n.type && <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800 text-[11px]">{n.type}</Badge>}
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${n.read_at ? 'text-gray-600 bg-gray-50 border-gray-200' : 'text-emerald-800 bg-emerald-50 border-emerald-200'}`}>
                                            {n.read_at ? <CheckCircle2 className="h-3.5 w-3.5 ml-1" /> : <Clock className="h-3.5 w-3.5 ml-1" />}
                                            {n.read_at ? 'خوانده شده' : 'خوانده نشده'}
                                        </span>
                                    </div>
                                    {n.message && <p className="text-xs md:text-sm text-gray-700 whitespace-pre-line">{n.message}</p>}
                                    <div className="mt-2 text-[11px] text-gray-500">تاریخ: {faDateTime(n.created_at)}</div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Pagination */}
                    <Card><CardContent className="py-4">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-600">نمایش:</span>
                                <select value={pageSize} onChange={e => { setPageSize(parseInt(e.target.value)); }} className="h-10 rounded-xl border border-emerald-300 bg-white px-3 text-sm">
                                    <option value={5}>۵</option><option value={10}>۱۰</option><option value={20}>۲۰</option><option value={50}>۵۰</option>
                                </select>
                            </div>
                            <div className="text-sm text-gray-700 bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100">
                                صفحه {fa.format(page)} از {fa.format(totalPages)} — {fa.format(totalItems)} اعلان
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><ChevronRight className="ml-1 h-4 w-4" /> قبلی</Button>
                                <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>بعدی <ChevronLeft className="mr-1 h-4 w-4" /></Button>
                            </div>
                        </div>
                    </CardContent></Card>
                </div>
            )}
        </div>
    );
};

export default NotificationsPage;
